import { useRef, useCallback, useEffect, useState } from 'react';
import html2canvas from 'html2canvas';
import { API_BASE } from '../utils/config';

// Queue for offline/failed uploads
const STORAGE_KEY_PREFIX = 'screenshot_queue_';

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const dataUrlToBlob = (dataUrl) => {
  const [meta, base64] = dataUrl.split(',');
  if (!meta || !base64) return null;

  const mimeMatch = meta.match(/data:(.*?);base64/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: mime });
};

// Lightweight on-screen toast to confirm screenshot status.
const showCapturePopup = (message, type = 'success') => {
  if (typeof document === 'undefined') return;

  const popup = document.createElement('div');
  popup.textContent = message;
  popup.style.position = 'fixed';
  popup.style.top = '20px';
  popup.style.right = '20px';
  popup.style.zIndex = '99999';
  popup.style.padding = '10px 14px';
  popup.style.borderRadius = '10px';
  popup.style.color = '#ffffff';
  popup.style.fontSize = '13px';
  popup.style.fontWeight = '600';
  popup.style.boxShadow = '0 8px 20px rgba(0,0,0,0.25)';
  popup.style.background = type === 'success' ? '#16a34a' : '#dc2626';
  popup.style.opacity = '0';
  popup.style.transform = 'translateY(-8px)';
  popup.style.transition = 'opacity 160ms ease, transform 160ms ease';

  document.body.appendChild(popup);

  requestAnimationFrame(() => {
    popup.style.opacity = '1';
    popup.style.transform = 'translateY(0)';
  });

  setTimeout(() => {
    popup.style.opacity = '0';
    popup.style.transform = 'translateY(-8px)';
    setTimeout(() => {
      if (popup.parentNode) {
        popup.parentNode.removeChild(popup);
      }
    }, 180);
  }, 2500);
};

/**
 * Custom hook for capturing and uploading interview screenshots
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.interviewId - The interview/room ID
 * @param {string} options.userId - The user ID
 * @param {string} options.role - 'candidate' or 'interviewer'
 * @param {number} options.captureInterval - Interval between captures in ms (default: 30000)
 * @param {number} options.maxWidth - Max image width for compression (default: 640)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.7)
 * @param {number} options.maxCaptures - Max successful screenshots to capture (default: Infinity)
 * @param {boolean} options.enabled - Whether capture is enabled (default: true)
 * @param {boolean} options.addWatermark - Whether to add timestamp watermark (default: true)
 */
const useScreenshotCapture = ({
  interviewId,
  userId,
  role = 'candidate',
  captureInterval = 30000, // 30 seconds default
  maxWidth = 640,
  quality = 0.7,
  maxCaptures = Infinity,
  enabled = true,
  addWatermark = true,
  captureMode = 'viewport', // 'viewport' captures current page, 'screen' uses getDisplayMedia
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const intervalRef = useRef(null);
  const uploadScreenshotRef = useRef(null);
  const screenStreamRef = useRef(null);
  const screenVideoRef = useRef(null);
  const captureCountRef = useRef(0);
  const [isCapturing, setIsCapturing] = useState(false);
  const [lastCaptureTime, setLastCaptureTime] = useState(null);
  const [captureCount, setCaptureCount] = useState(0);
  const [error, setError] = useState(null);
  const [queueLength, setQueueLength] = useState(0);

  useEffect(() => {
    captureCountRef.current = captureCount;
  }, [captureCount]);

  const hasReachedCaptureLimit = useCallback(() => {
    if (!Number.isFinite(maxCaptures)) return false;
    if (maxCaptures <= 0) return true;
    return captureCountRef.current >= maxCaptures;
  }, [maxCaptures]);

  // Get queue from localStorage
  const getQueue = useCallback(() => {
    if (!interviewId) return [];
    try {
      const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${interviewId}`);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, [interviewId]);

  // Save queue to localStorage
  const saveQueue = useCallback((queue) => {
    if (!interviewId) return;
    try {
      localStorage.setItem(`${STORAGE_KEY_PREFIX}${interviewId}`, JSON.stringify(queue));
      setQueueLength(queue.length);
    } catch (e) {
      console.error('Failed to save screenshot queue:', e);
    }
  }, [interviewId]);

  // Add to queue for retry
  const addToQueue = useCallback(async (screenshotData) => {
    if (!screenshotData?.blob || !(screenshotData.blob instanceof Blob)) {
      return;
    }

    const queue = getQueue();
    const blobDataUrl = await blobToDataUrl(screenshotData.blob);

    queue.push({
      blobDataUrl,
      blobType: screenshotData.blob.type || 'image/jpeg',
      timestamp: screenshotData.timestamp,
      queuedAt: new Date().toISOString(),
      retryCount: 0,
    });
    saveQueue(queue);
  }, [getQueue, saveQueue]);

  // Process queued uploads
  const processQueue = useCallback(async () => {
    if (!interviewId || !userId) return;

    const queue = getQueue();
    if (queue.length === 0) return;

    const newQueue = [];
    for (const item of queue) {
      if (item.retryCount >= 3) {
        console.warn('Screenshot dropped after 3 retries:', item);
        continue;
      }

      let retryBlob = null;
      if (item.blobDataUrl && typeof item.blobDataUrl === 'string') {
        retryBlob = dataUrlToBlob(item.blobDataUrl);
      }

      if (!(retryBlob instanceof Blob)) {
        console.warn('Dropping invalid queued screenshot payload:', item);
        continue;
      }

      const success = await uploadScreenshotRef.current?.(retryBlob, item.timestamp, true);
      if (!success) {
        newQueue.push({ ...item, retryCount: item.retryCount + 1 });
      }
    }
    saveQueue(newQueue);
  }, [getQueue, saveQueue, interviewId, userId]);

  // Add watermark to canvas
  const addWatermarkToCanvas = useCallback((ctx, width, height, timestamp) => {
    const text = `${role.toUpperCase()} | ${new Date(timestamp).toLocaleString()}`;
    
    ctx.font = '14px Arial';
    const textWidth = ctx.measureText(text).width;
    
    // Background for watermark
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(10, height - 30, textWidth + 20, 24);
    
    // Watermark text
    ctx.fillStyle = '#ffffff';
    ctx.fillText(text, 20, height - 12);
  }, [role]);

  const initScreenCapture = useCallback(async () => {
    if (typeof window === 'undefined' || !navigator.mediaDevices?.getDisplayMedia) {
      throw new Error('Screen capture is not supported in this browser');
    }

    if (screenStreamRef.current && screenVideoRef.current) {
      return true;
    }

    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        cursor: 'always',
      },
      audio: false,
    });

    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;

    await new Promise((resolve, reject) => {
      video.onloadedmetadata = () => {
        video.play().then(resolve).catch(reject);
      };
      video.onerror = () => reject(new Error('Failed to initialize screen capture video'));
    });

    const track = stream.getVideoTracks()[0];
    if (track) {
      track.onended = () => {
        screenStreamRef.current = null;
        screenVideoRef.current = null;
        setError('Screen sharing stopped. Falling back to webcam capture if available.');
      };
    }

    screenStreamRef.current = stream;
    screenVideoRef.current = video;
    return true;
  }, []);

  const stopScreenCapture = useCallback(() => {
    if (screenStreamRef.current) {
      for (const track of screenStreamRef.current.getTracks()) {
        track.stop();
      }
      screenStreamRef.current = null;
    }

    if (screenVideoRef.current) {
      screenVideoRef.current.pause();
      screenVideoRef.current.srcObject = null;
      screenVideoRef.current = null;
    }
  }, []);

  // Capture screenshot from current page viewport, screen stream, or webcam fallback.
  const captureFrame = useCallback(async () => {
    if (!canvasRef.current) {
      console.warn('Canvas ref not available for screenshot capture');
      console.warn('❌ Screenshot not taken: canvas reference missing');
      showCapturePopup('Screenshot not taken (capture not ready)', 'error');
      return null;
    }

    try {
      const canvas = canvasRef.current;
      const timestamp = new Date().toISOString();

      if (captureMode === 'viewport') {
        const target = document.getElementById('root') || document.body;
        if (!target) {
          throw new Error('Page root element not found for viewport capture');
        }

        const renderedCanvas = await html2canvas(target, {
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          scale: 1,
        });

        const width = Math.min(renderedCanvas.width, maxWidth);
        const height = Math.round((renderedCanvas.height * width) / renderedCanvas.width);

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(renderedCanvas, 0, 0, width, height);

        if (addWatermark) {
          addWatermarkToCanvas(ctx, width, height, timestamp);
        }

        return new Promise((resolve) => {
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve({ blob, timestamp });
              } else {
                resolve(null);
              }
            },
            'image/jpeg',
            quality
          );
        });
      }

      const primaryVideo = captureMode === 'screen' ? screenVideoRef.current : videoRef.current;
      const fallbackVideo = captureMode === 'screen' ? videoRef.current : null;
      const video = primaryVideo || fallbackVideo;

      if (!video) {
        console.warn('Video source not available for screenshot capture');
        showCapturePopup('Screenshot not taken (video source missing)', 'error');
        return null;
      }

      // Check if video is playing
      if (video.readyState < 2 || video.videoWidth === 0) {
        console.warn('Video not ready for capture');
        console.warn('❌ Screenshot not taken: video stream not ready');
        showCapturePopup('Screenshot not taken (video stream not ready)', 'error');
        return null;
      }

      // Calculate dimensions maintaining aspect ratio
      const aspectRatio = video.videoHeight / video.videoWidth;
      const width = Math.min(video.videoWidth, maxWidth);
      const height = Math.round(width * aspectRatio);

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, width, height);

      if (addWatermark) {
        addWatermarkToCanvas(ctx, width, height, timestamp);
      }

      // Convert to blob
      return new Promise((resolve) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve({ blob, timestamp });
            } else {
              resolve(null);
            }
          },
          'image/jpeg',
          quality
        );
      });
    } catch (e) {
      console.error('Error capturing frame:', e);
      console.error('❌ Screenshot not taken: capture frame failed');
      showCapturePopup('Screenshot not taken (capture failed)', 'error');
      setError(e.message);
      return null;
    }
  }, [captureMode, maxWidth, quality, addWatermark, addWatermarkToCanvas]);

  // Upload screenshot to server
  const uploadScreenshot = useCallback(async (blob, timestamp, isRetry = false) => {
    if (!blob || !interviewId || !userId) {
      if (!isRetry) {
        console.warn('Missing required data for screenshot upload');
        console.warn('❌ Screenshot not taken: missing interview/user metadata');
        showCapturePopup('Screenshot not taken (missing metadata)', 'error');
      }
      return false;
    }

    try {
      const formData = new FormData();
      formData.append('roomId', interviewId);
      formData.append('interviewId', interviewId);
      formData.append('userId', userId);
      formData.append('role', role);
      formData.append('timestamp', timestamp);
      formData.append('screenshot', blob, `screenshot_${Date.now()}.jpg`);

      const response = await fetch(`${API_BASE}/api/screenshots/upload/${interviewId}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Screenshot uploaded:', result);
      console.log('✅ Screenshot taken successfully');
      showCapturePopup('Screenshot taken successfully', 'success');
      
      if (!isRetry) {
        setCaptureCount((prev) => {
          const next = prev + 1;
          captureCountRef.current = next;
          return next;
        });
        setLastCaptureTime(new Date());
      }
      
      setError(null);
      return true;
    } catch (e) {
      console.error('Screenshot upload error:', e);
      console.error('❌ Screenshot not taken: upload failed');
      showCapturePopup('Screenshot not taken (upload failed)', 'error');
      setError(e.message);
      
      // Add to queue for retry if not already a retry
      if (!isRetry && blob instanceof Blob) {
        await addToQueue({ blob, timestamp });
      }
      
      return false;
    }
  }, [interviewId, userId, role, addToQueue]);

  useEffect(() => {
    uploadScreenshotRef.current = uploadScreenshot;
  }, [uploadScreenshot]);

  // Capture and upload single screenshot
  const captureAndUpload = useCallback(async () => {
    if (hasReachedCaptureLimit()) {
      return false;
    }

    const result = await captureFrame();
    if (result) {
      await uploadScreenshot(result.blob, result.timestamp);
    } else {
      console.warn('❌ Screenshot not taken in this attempt');
    }
    return result !== null;
  }, [captureFrame, uploadScreenshot, hasReachedCaptureLimit]);

  const scheduleCaptureTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    intervalRef.current = setInterval(() => {
      captureAndUpload();
    }, captureInterval);
  }, [captureAndUpload, captureInterval]);

  // Start automatic capture
  const startCapture = useCallback(async (videoElement) => {
    if (!enabled) {
      console.log('Screenshot capture is disabled');
      return;
    }

    if (videoElement) {
      videoRef.current = videoElement;
    }

    if (captureMode === 'screen') {
      try {
        await initScreenCapture();
        console.log('🖥️ Fullscreen screenshot mode enabled');
      } catch (e) {
        console.warn('⚠️ Fullscreen capture unavailable, falling back to webcam capture:', e.message);
        setError(`Fullscreen capture unavailable: ${e.message}`);
      }
    }

    if (captureMode !== 'viewport' && !screenVideoRef.current && !videoRef.current) {
      console.warn('No valid source available for screenshot capture');
      return;
    }

    setIsCapturing(true);
    setError(null);

    console.log(`📸 Starting screenshot capture for ${role} every ${captureInterval / 1000}s`);

    // Initial capture
    setTimeout(() => {
      captureAndUpload();
    }, 2000); // Wait 2 seconds for video to stabilize

    // Periodic capture
    scheduleCaptureTimer();

    // Process any queued uploads
    processQueue();
  }, [enabled, captureMode, initScreenCapture, role, captureInterval, captureAndUpload, processQueue, scheduleCaptureTimer]);

  // Stop automatic capture
  const stopCapture = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    stopScreenCapture();
    setIsCapturing(false);
    console.log('📸 Screenshot capture stopped');
  }, [stopScreenCapture]);

  useEffect(() => {
    if (!isCapturing) return;
    if (!Number.isFinite(maxCaptures)) return;
    if (captureCount >= maxCaptures) {
      console.log(`📸 Capture limit reached (${captureCount}/${maxCaptures}). Stopping capture.`);
      stopCapture();
    }
  }, [captureCount, maxCaptures, isCapturing, stopCapture]);

  useEffect(() => {
    if (!isCapturing) return;
    if (hasReachedCaptureLimit()) return;
    scheduleCaptureTimer();
    console.log(`📸 Capture interval updated for ${role}: every ${captureInterval / 1000}s`);
  }, [captureInterval, isCapturing, hasReachedCaptureLimit, role, scheduleCaptureTimer]);

  // Initialize canvas on mount
  useEffect(() => {
    canvasRef.current = document.createElement('canvas');

    // Load any queued uploads from storage
    processQueue();

    return () => {
      stopCapture();
    };
  }, [processQueue, stopCapture]);

  // Manual capture trigger
  const captureNow = useCallback(async () => {
    if (captureMode !== 'viewport' && !videoRef.current && !screenVideoRef.current) {
      console.warn('No video element set for manual capture');
      return false;
    }
    return captureAndUpload();
  }, [captureAndUpload, captureMode]);

  // Update video reference (useful when video element changes)
  const setVideoElement = useCallback((element) => {
    videoRef.current = element;
  }, []);

  // Final capture on interview end
  const captureOnEnd = useCallback(async () => {
    console.log('📸 Capturing final screenshot on interview end');
    const result = await captureNow();
    stopCapture();
    return result;
  }, [captureNow, stopCapture]);

  return {
    // Control functions
    startCapture,
    stopCapture,
    captureNow,
    captureOnEnd,
    setVideoElement,
    
    // State
    isCapturing,
    lastCaptureTime,
    captureCount,
    error,
    queueLength,
    
    // Refs (for direct access if needed)
    videoRef,
    canvasRef,
  };
};

// Utility function to fetch screenshots for an interview
export const fetchInterviewScreenshots = async (interviewId) => {
  try {
    const response = await fetch(`${API_BASE}/api/screenshots/${encodeURIComponent(interviewId)}`);
    if (response.status === 404) {
      // Some deployments return 404 when no screenshots exist yet.
      return { success: true, screenshots: [] };
    }
    if (!response.ok) {
      throw new Error(`Failed to fetch screenshots: ${response.status}`);
    }
    return await response.json();
  } catch (e) {
    console.error('Error fetching screenshots:', e);
    throw e;
  }
};

export default useScreenshotCapture;
